<template>
    <div class="vdatetime-calendar multi-date-calendar">
        <div class="vdatetime-calendar__navigation">
            <div class="vdatetime-calendar__navigation--previous" @click="previousMonth">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 61.3 102.8">
                    <path fill="none" stroke="#444" stroke-width="14" stroke-miterlimit="10" d="M56.3 97.8L9.9 51.4 56.3 5"/>
                </svg>
            </div>
            <div class="vdatetime-calendar__current--month">{{ monthName }} {{ newYear }}</div>
            <div class="vdatetime-calendar__navigation--next" @click="nextMonth">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 61.3 102.8">
                    <path fill="none" stroke="#444" stroke-width="14" stroke-miterlimit="10" d="M56.3 97.8L9.9 51.4 56.3 5"/>
                </svg>
            </div>
        </div>
        
        <div class="vdatetime-calendar__month">
            <div class="vdatetime-calendar__month__weekday" v-for="weekday in weekdays" :key="weekday">
                {{ weekday }}
            </div>
            <div 
                class="vdatetime-calendar__month__day" 
                v-for="(day, index) in days" 
                :key="index"
                @click="selectDay(day)" 
                :class="{
                    'vdatetime-calendar__month__day--selected': day.selected, 
                    'vdatetime-calendar__month__day--disabled': day.disabled
                }">
                <span><span>{{ day.number }}</span></span>
            </div>
        </div>

        <div class="multi-date-calendar__footer" v-if="selectedDates.length > 0">
            <div class="selected-dates-preview">
                <strong>{{ locale === 'en' ? 'Selected dates:' : '已選擇的日期：' }}</strong>
                <div class="selected-dates-list">
                    <span 
                        v-for="date in sortedSelectedDates" 
                        :key="date"
                        class="selected-date-badge"
                        @click="removeDate(date)">
                        {{ formatDate(date) }}
                        <i class="fas fa-times"></i>
                    </span>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { DateTime, Info } from 'luxon'

function monthDays(year, month, weekStart) {
    const monthDate = DateTime.fromObject({ year, month, day: 1, zone: 'UTC' })
    let firstDay = monthDate.weekday - weekStart

    if (firstDay < 0) {
        firstDay += 7
    }
    
    let lastDay = (weekStart - monthDate.weekday - monthDate.daysInMonth) % 7
    if (lastDay < 0) {
        lastDay += 7
    }

    return new Array(monthDate.daysInMonth + firstDay + lastDay)
        .fill(null)
        .map((value, index) => {
            return (index + 1 <= firstDay || index >= firstDay + monthDate.daysInMonth) 
                ? null 
                : (index + 1 - firstDay)
        })
}

function monthDayIsDisabled(minDate, maxDate, year, month, day) {
    const date = DateTime.fromObject({ year, month, day, zone: 'UTC' })
    const minDateStart = minDate ? minDate.startOf('day') : null
    const maxDateStart = maxDate ? maxDate.startOf('day') : null

    return (minDateStart && date < minDateStart) || (maxDateStart && date > maxDateStart)
}

function weekdays(weekStart, locale) {
    if (--weekStart < 0) {
        weekStart = 6
    }

    const luxonLocale = locale === 'en' ? 'en' : 'zh-HK'
    const weekDays = Info.weekdays('short', { locale: luxonLocale })
    return weekDays.concat(weekDays.splice(0, weekStart))
}

function months(locale) {
    const luxonLocale = locale === 'en' ? 'en' : 'zh-HK'
    return Info.months('long', { locale: luxonLocale })
}

export default {
    name: 'MultiDateCalendar',

    props: {
        year: {
            type: Number,
            required: true
        },
        month: {
            type: Number,
            required: true
        },
        selectedDates: {
            type: Array,
            default: function() {
                return []
            }
        },
        minDate: {
            type: DateTime,
            default: null
        },
        maxDate: {
            type: DateTime,
            default: null
        },
        weekStart: {
            type: Number,
            default: 1
        },
        maxSelections: {
            type: Number,
            default: null
        },
        locale: {
            type: String,
            default: 'en'
        }
    },

    data() {
        return {
            newDate: DateTime.fromObject({ year: this.year, month: this.month, zone: 'UTC' })
        }
    },

    computed: {
        luxonLocale() {
            return this.locale === 'en' ? 'en' : 'zh-HK'
        },

        weekdays() {
            return weekdays(this.weekStart, this.locale)
        },

        months() {
            return months(this.locale)
        },

        newYear() {
            return this.newDate.year
        },

        newMonth() {
            return this.newDate.month
        },

        monthName() {
            return this.months[this.newMonth - 1]
        },

        days() {
            return monthDays(this.newYear, this.newMonth, this.weekStart).map(day => {
                if (!day) {
                    return {
                        number: null,
                        selected: false,
                        disabled: true
                    }
                }

                const dateString = DateTime.fromObject({
                    year: this.newYear,
                    month: this.newMonth,
                    day,
                    zone: 'UTC'
                }).toISODate()

                return {
                    number: day,
                    dateString: dateString,
                    selected: this.selectedDates.includes(dateString),
                    disabled: monthDayIsDisabled(this.minDate, this.maxDate, this.newYear, this.newMonth, day)
                }
            })
        },

        sortedSelectedDates() {
            return [...this.selectedDates].sort()
        }
    },

    methods: {
        selectDay(day) {
            if (day.disabled || !day.number) {
                return
            }

            // Check max selections limit
            if (this.maxSelections &&
                !day.selected &&
                this.selectedDates.length >= this.maxSelections) {
                return
            }

            this.$emit('toggle-date', day.dateString)
        },

        removeDate(dateString) {
            this.$emit('toggle-date', dateString)
        },

        previousMonth() {
            this.newDate = this.newDate.minus({ months: 1 })
            this.$emit('update:year', this.newYear)
            this.$emit('update:month', this.newMonth)
        },

        nextMonth() {
            this.newDate = this.newDate.plus({ months: 1 })
            this.$emit('update:year', this.newYear)
            this.$emit('update:month', this.newMonth)
        },

        formatDate(dateString) {
            return DateTime.fromISO(dateString).toFormat('yyyy-MM-dd')
        }
    },

    watch: {
        year(newYear) {
            if (newYear !== this.newYear) {
                this.newDate = DateTime.fromObject({
                    year: newYear,
                    month: this.newMonth,
                    zone: 'UTC'
                })
            }
        },
        month(newMonth) {
            if (newMonth !== this.newMonth) {
                this.newDate = DateTime.fromObject({
                    year: this.newYear,
                    month: newMonth,
                    zone: 'UTC'
                })
            }
        }
    }
}
</script>

<style scoped>
.multi-date-calendar__footer {
    padding: 12px 15px;
    border-top: 1px solid rgba(0, 0, 0, 0.1);
}

.selected-dates-preview {
    font-size: 14px;
}

.selected-dates-preview strong {
    display: block;
    margin-bottom: 8px;
    color: #333;
}

.selected-dates-list {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
}

.selected-date-badge {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 12px;
    background-color: #3f51b5;
    color: white;
    border-radius: 14px;
    font-size: 14px;
    cursor: pointer;
    transition: background-color 0.2s;
}

.selected-date-badge:hover {
    background-color: #303f9f;
}

.selected-date-badge i {
    font-size: 12px;
    opacity: 0.8;
}

.selected-date-badge i:hover {
    opacity: 1;
}
</style>




// WEBPACK FOOTER //
// resources/assets/js/components/DatePicker/MultiDateCalendar.vue